import { Controller } from "@hotwired/stimulus"

// The homepage's 30-second product walkthrough. No `autoplay` attribute on
// the <video> itself — starting it here, after checking prefers-reduced-motion,
// means a reduced-motion visitor never sees a frame of it move before it stops.
// It plays muted, which is what lets play() start without a click at all; the
// pause control exists because thirty looping seconds of motion still needs a
// way to stop it (WCAG 2.2.2), not because sound is at stake.
export default class extends Controller {
  static targets = ["video", "button", "playIcon", "pauseIcon", "label"]

  connect() {
    this.userPaused = false
    this.onPlay = () => this.render(true)
    this.onPause = () => this.render(false)
    this.videoTarget.addEventListener("play", this.onPlay)
    this.videoTarget.addEventListener("pause", this.onPause)

    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (!this.reducedMotion) this.attemptPlay()

    // Off screen, thirty seconds of looping motion is just a battery drain.
    // Only resumes what the visitor didn't stop themselves.
    this.observer = new IntersectionObserver(([ entry ]) => {
      if (entry.isIntersecting) {
        if (!this.userPaused && !this.reducedMotion) this.attemptPlay()
      } else {
        this.videoTarget.pause()
      }
    }, { threshold: 0.25 })
    this.observer.observe(this.videoTarget)
  }

  toggle() {
    if (this.videoTarget.paused) {
      this.userPaused = false
      this.attemptPlay()
    } else {
      this.userPaused = true
      this.videoTarget.pause()
    }
  }

  // Autoplay can be refused even though the video is muted, and a scripted
  // play() from the intersection callback has no user gesture behind it
  // either. Both are silent no-ops, not errors — the poster and the paused
  // button are the fallback a visitor sees instead.
  attemptPlay() {
    this.videoTarget.play().catch(() => {})
  }

  render(playing) {
    this.buttonTarget.setAttribute("aria-pressed", String(playing))
    this.playIconTarget.classList.toggle("hidden", playing)
    this.pauseIconTarget.classList.toggle("hidden", !playing)
    this.labelTarget.textContent = playing ? "Pause" : "Play"
  }

  disconnect() {
    this.videoTarget.removeEventListener("play", this.onPlay)
    this.videoTarget.removeEventListener("pause", this.onPause)
    this.observer.disconnect()
  }
}
